import { ref, unref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { useSettingStore } from '@/stores/setting'
import { ROUTES } from '@/router/routes.enum'
import { reportError } from '@/services/error-reporting'
import { UserRole } from '@/utils/constants'
import { setCookie } from '@/services/cookies'
import { SIGN_OUT_REASONS } from '@/utils/constants'

export function useAutoLogout() {
  const router = useRouter()
  const sessionStore = useSessionStore()
  const settingStore = useSettingStore()
  const timer = ref<NodeJS.Timeout | null>(null)
  const isActive = ref(false)
  const timeoutMinutes = ref(0)
  // Events that indicate user activity
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

  const logout = async () => {
    const result = await sessionStore.signOut(SIGN_OUT_REASONS.POS_INACTIVITY)

    if (!result.error) {
      setCookie('signin-auto-logout', 'true')

      const currentPath = router.currentRoute.value.fullPath
      const redirectUrl = new URL(router.resolve({ name: ROUTES.SIGN_IN }).fullPath, window.location.origin)
      redirectUrl.searchParams.set('redirect', currentPath)

      window.location.replace(redirectUrl.toString())
    } else {
      reportError('Error when signing out due to inactivity', {
        context: { reason: result },
        tags: 'auto-logout'
      })
    }
  }

  const resetTimer = () => {
    if (timer.value) clearTimeout(timer.value)

    timer.value = setTimeout(
      () => {
        logout()
      },
      timeoutMinutes.value * 60 * 1000
    )
  }

  const setupEventListeners = () => {
    events.forEach((event) => {
      window.addEventListener(event, resetTimer, true)
    })
  }

  const removeEventListeners = () => {
    events.forEach((event) => {
      window.removeEventListener(event, resetTimer, true)
    })
  }

  async function startAutoLogout() {
    if (isActive.value) return
    if (!settingStore.loadedPublic) return

    const user = sessionStore.user
    if (!user) return

    if (user && user.roles.length == 1 && user.roles[0] == UserRole.SALES) {
      timeoutMinutes.value = Number(settingStore.publicSettings.pos_inactive_timeout)

      if (timeoutMinutes.value && timeoutMinutes.value > 0) {
        setupEventListeners()
        resetTimer()
      }
    }

    isActive.value = true
  }

  const stopAutoLogout = () => {
    if (isActive.value) {
      removeEventListeners()
      if (timer.value) clearTimeout(timer.value)
      isActive.value = false
    }
  }

  watch(
    () => [sessionStore.user, unref(settingStore.loadedPublic)],
    () => {
      if (!isActive.value) {
        startAutoLogout()
      }
    }
  )

  return {
    startAutoLogout,
    stopAutoLogout,
    logout,
    isActive
  }
}
